import { Injectable, inject } from '@angular/core';
import type { AbstractControl } from '@angular/forms';

import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { Subject, of, switchMap, takeUntil, merge, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';

import { I18nService } from '@core/i18n-legacy';
import { LocationSelectors } from '@core/locations-data';
import { EmploymentType } from '@core/offers-types';
import type { Requisition } from '@core/requisitions-types';
import { RequisitionStatus } from '@core/requisitions-types';

import type { RequisitionsAbilities } from '../../data/single-requisition/requisitions-data-provider.service';
import { RequisitionsDataProviderService } from '../../data/single-requisition/requisitions-data-provider.service';
import { RequisitionFormManagerService } from '../../services/requisition-form-manager.service';

export type RequestApprovalDisabled = false | { message: string };

@Injectable()
export class RequisitionsGeneralFacade {
  private readonly i18n = inject(I18nService);
  private readonly store = inject(Store);
  private readonly requisitionFormManager = inject(RequisitionFormManagerService);
  protected readonly requisitionsData = inject(RequisitionsDataProviderService);

  private requisition: Requisition;
  private abilities: RequisitionsAbilities;
  private isRequisitionReadOnly: boolean;
  private ngOnDestroy$ = new Subject<void>();

  constructor() {
    const requisitionsData = this.requisitionsData;

    requisitionsData.data$
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(({ requisition, abilities, isRequisitionReadOnly }) => {
        this.requisition = requisition;
        this.abilities = abilities;
        this.isRequisitionReadOnly = isRequisitionReadOnly;
      });
  }

  public get hasManageRequisitionActions(): boolean {
    return this.abilities.manageRequisitions;
  }

  public get isReadOnlyMode(): boolean {
    return this.isRequisitionReadOnly;
  }

  public get isFormEditable(): boolean {
    const allowedStatuses = [RequisitionStatus.Draft, RequisitionStatus.Rejected];
    return (
      this.abilities.manageRequisitions &&
      allowedStatuses.includes(this.requisition.status) &&
      !this.isRequisitionReadOnly
    );
  }

  public get isTitleEditable(): boolean {
    return this.abilities.manageRequisitions && !this.isRequisitionReadOnly;
  }

  public get canManageFiles(): boolean {
    return this.abilities.manageRequisitions && !this.isRequisitionReadOnly;
  }

  // TODO - whole logic for those actions (see openings-form.component) should be extracted
  public get canDeleteOpeningsAndUnassign(): boolean {
    return this.abilities.manageRequisitions && !this.isRequisitionReadOnly;
  }

  public getRequestApprovalDisabledState(
    allApproversDeleted: boolean,
  ): Observable<RequestApprovalDisabled> {
    if (!this.requisition.approvalFlowId)
      return of({
        message: this.i18n.translate(
          'app.requisitions.details_page.request_approval_disabled.flow_missing',
        ),
      });
    if (allApproversDeleted)
      return of({
        message: this.i18n.translate(
          'app.requisitions.details_page.request_approval_disabled.no_approvers',
        ),
      });

    const locationsControl: AbstractControl<number[]> | null =
      this.requisitionFormManager.getControl('locationIds');
    const locationsErrors$ = this.getLocationsErrors(locationsControl);
    const employmentTypeControl: AbstractControl<string> | null =
      this.requisitionFormManager.getControl('employmentType');
    const employmentTypeErrors$ = this.getEmploymentTypeErrors(employmentTypeControl);

    return combineLatest([locationsErrors$, employmentTypeErrors$]).pipe(
      map(
        ([locationsError, employmentTypeError]) =>
          [
            ...(employmentTypeError ? [employmentTypeError] : []),
            ...(locationsError ? [locationsError] : []),
          ][0] ?? false,
      ),
    );
  }

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  private getLocationsErrors(
    locationsControl: AbstractControl<number[]> | null,
  ): Observable<RequestApprovalDisabled> {
    return locationsControl
      ? merge(locationsControl.valueChanges, of(locationsControl.value)).pipe(
          switchMap(locationIds => this.store.select(LocationSelectors.byIds(locationIds))),
          map(locations =>
            locations.length &&
            locations.every(location => location.archivedAt || !location.isValid)
              ? {
                  message: this.i18n.translate(
                    'app.requisitions.details_page.request_approval_disabled.no_active_locations',
                  ),
                }
              : false,
          ),
        )
      : of(false);
  }

  private getEmploymentTypeErrors(
    employmentTypeControl: AbstractControl<string> | null,
  ): Observable<RequestApprovalDisabled> {
    return employmentTypeControl
      ? merge(employmentTypeControl.valueChanges, of(employmentTypeControl.value)).pipe(
          map(employmentType => {
            const isInvalid = [EmploymentType.FullTime, EmploymentType.PartTime].includes(
              <EmploymentType>employmentType,
            );
            if (isInvalid) {
              return {
                message: this.i18n.translate(
                  'app.requisitions.details_page.request_approval_disabled.specify_employment_type',
                ),
              };
            }
            return false;
          }),
        )
      : of(false);
  }
}
